/**
 * The send ledger as a funnel: every enrolled address, by what became of it.
 *
 * READ TOP TO BOTTOM IT IS THE LIFE OF ONE ADDRESS - waiting, screened out,
 * refused by the transport, gone, and then the four things a reader can do
 * about it. That order is the reason the card is legible without a legend, and
 * it lives in `LEDGER_ORDER` rather than in this file's markup so the funnel and
 * the arithmetic cannot fall out of step.
 *
 * EVERY STATUS IS DRAWN, INCLUDING THE EMPTY ONES. A row that is missing
 * because it is zero looks exactly like a row nobody thought of, and this
 * module has already shipped one column that could only ever say zero. A zero
 * that is drawn is a zero somebody chose.
 *
 * The share is against everything enrolled, not against the step above. A
 * step-by-step funnel would make "skipped" a share of nothing in particular,
 * because a skipped address never entered the queue at all.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { StatusTotals } from "../../../../lib/marketingOverview";
import { Bar, Panel, num, pct } from "../shared";
import { CONTACTED, LEDGER_ORDER, count, enrolledTotal } from "./lib";

/** Which statuses are worth alarm, which are simply where a row waits. */
function toneOf(status: string): "good" | "warn" | "bad" | "muted" | "neutral" {
  if (status === "replied") return "good";
  if (status === "bounced" || status === "failed") return "warn";
  if (status === "complained") return "bad";
  if (status === "pending" || status === "skipped" || status === "sending") return "muted";
  return "neutral";
}

export function LedgerCard({ ledger }: { ledger: StatusTotals }) {
  const t = useT();
  const enrolled = enrolledTotal(ledger);

  return (
    <Panel
      title={t("marketing.overview.ledger.title")}
      description={
        <>
          {t("marketing.overview.ledger.intro", { n: num(enrolled) })}
          {/* The legend only when there is something for it to describe. On an
              empty ledger it explains a weight that nothing on screen carries. */}
          {enrolled > 0 ? <> {t("marketing.overview.ledger.contactedMark")}</> : null}
        </>
      }
    >
      {enrolled === 0 ? (
        <p className="crm-sub">{t("marketing.overview.ledger.empty")}</p>
      ) : (
        <div className="grid gap-x-8 gap-y-1 sm:grid-cols-2">
          {LEDGER_ORDER.map((status) => {
            const n = count(ledger, status);
            const contacted = CONTACTED.has(status);
            return (
              <div key={status} className="min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  {/* The statuses that mean a letter left the building are
                      marked, because their sum IS the headline tile and a
                      reader should not have to know the rule to see that.
                      Marked by weight rather than by a word repeated on five of
                      the nine rows - the card is a column of short labels, and
                      an extra word on each turns it into a paragraph. */}
                  <span className={contacted ? "crm-sub truncate font-semibold" : "crm-sub truncate"}>
                    {t(`marketing.recipientStatus.${status}`)}
                  </span>
                  <b className={contacted ? "crm-tnum shrink-0 text-[13.5px]" : "crm-mut crm-tnum shrink-0 text-[13.5px] font-normal"}>
                    {num(n)}
                  </b>
                </div>
                <div className="flex items-center gap-2.5">
                  <Bar value={n} total={enrolled} tone={toneOf(status)} className="min-w-0 flex-1" />
                  <span className="crm-mut crm-tnum shrink-0 text-[11px]">{pct(n, enrolled)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
